import { ApplicationRef, computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AiCreditsTopupService } from './ai-credits-topup.service';

@Injectable({ providedIn: 'root' })
export class AiCreditsService {
	static readonly THRESHOLD_LOW = 25;           // ≤ this %: red level (1 bar active)
	static readonly THRESHOLD_MEDIUM = 50;        // ≤ this %: orange level (2 bars active)
	static readonly THRESHOLD_HIGH = 75;          // ≤ this %: 3 bars active; > this: 4 bars

	private readonly http = inject(HttpClient);
	private readonly topupService = inject(AiCreditsTopupService);
	private readonly appRef = inject(ApplicationRef);

	private readonly balanceUrl = `${environment.apiBaseUrl}${environment.endpoint.credits.balance}`;

	private readonly _balance = signal<number>(0);
	private readonly _balanceLoaded = signal(false);
	private _fetchingBalance = false;

	/** Same GET /credits/config the Vipps top-up dialog uses — reused here (rather than a
	 * second fetch) so the signal bar's scale (starterNok) and low-balance badge threshold
	 * (lowBalanceThresholdNok) come from the server instead of a hardcoded 100-unit scale. */
	private readonly config = this.topupService.config;

	readonly balance = this._balance.asReadonly();
	readonly balanceLoaded = this._balanceLoaded.asReadonly();
	readonly percentage = computed(() => {
		const starterNok = this.config()?.starterNok;
		if (!starterNok) return 0;
		return Math.min(100, Math.round((this._balance() / starterNok) * 100));
	});
	readonly isLow = computed(() => {
		const threshold = this.config()?.lowBalanceThresholdNok;
		if (threshold == null) return false;
		const balance = this._balance();
		return balance > 0 && balance <= threshold;
	});
	readonly isEmpty = computed(() => this._balance() <= 0);

	readonly levelClass = computed((): 'level-green' | 'level-orange' | 'level-red' | 'level-empty' => {
		const p = this.percentage();
		if (p <= 0) return 'level-empty';
		if (p <= AiCreditsService.THRESHOLD_LOW) return 'level-red';
		if (p <= AiCreditsService.THRESHOLD_MEDIUM) return 'level-orange';
		return 'level-green';
	});

	readonly activeBars = computed(() => {
		const p = this.percentage();
		return {
			bar1: p >= 1,
			bar2: p > AiCreditsService.THRESHOLD_LOW,
			bar3: p > AiCreditsService.THRESHOLD_MEDIUM,
			bar4: p > AiCreditsService.THRESHOLD_HIGH,
		};
	});

	fetchBalance(): void {
		if (this._fetchingBalance) return;
		this._fetchingBalance = true;
		// Piggybacks the config load onto every balance fetch (idempotent — fetchConfig()
		// no-ops while a request is in flight or once config() is set) instead of leaving it to a
		// single call site: fetchBalance() already runs on app init AND on every re-entry into the
		// Chat tab, so a transient config-fetch failure gets a natural retry next time either fires,
		// rather than permanently pinning percentage()/isLow() at their null-config defaults.
		this.topupService.fetchConfig();
		this.http.get<{ balanceNok: number }>(this.balanceUrl).subscribe({
			next: ({ balanceNok }) => { this._fetchingBalance = false; this.applyBalanceUpdate(balanceNok); },
			error: (err) => { this._fetchingBalance = false; console.error('[AiCredits] Failed to fetch balance:', err); },
		});
	}

	/** Public so external balance sources (the Vipps top-up SSE push, the topup-status
	 * poll response) can update the same signal the balance fetch feeds.
	 *
	 * Only clamps the floor at 0 — starterNok is a percentage-bar reference point (see
	 * `percentage`, which separately saturates at 100%), not a ceiling on the real balance.
	 * A Vipps top-up routinely pushes the true NOK balance above starterNok; clamping the
	 * underlying value here would silently show a wrong, capped number everywhere the
	 * balance is displayed (not just the bars). */
	applyBalanceUpdate(raw: unknown): void {
		if (typeof raw !== 'number' || !isFinite(raw)) return;
		this._balance.set(Math.max(0, raw));
		this._balanceLoaded.set(true);
		// Forces an immediate, synchronous render instead of relying on zone.js's automatic
		// stability-triggered tick (FT-887): confirmed intermittently unreliable for updates
		// originating from this call chain (sse.js manages its own XHR outside Angular's normal
		// event flow), even though the signal write itself is always correct and immediate.
		this.appRef.tick();
	}
}
